import PortalIdParser from 'PortalIdParser';
import { UI_MODE } from 'framework-listing-lib/constants';
import { PropertyObjectTypes } from 'FormsUI/constants/PropertyObjectTypes';
import { Tabs } from 'FormsUI/constants/Submissions';
import Routes from 'ContentUtils/Routes';
import { FormCreationTypes } from 'FormsUI/constants/FormCreation';
import getSubmissionsAppRoute from 'forms-utils-lib/urls/submissions/getSubmissionsAppRoute';
import { getUpdatedSearchParams, getFilteredSearchParams } from 'FormsUI/utils/QueryParams';
import { getWindowLocationSearch, getWindowLocationPathname, getWindowOrigin } from 'FormsUI/utils/WindowGetters';
import { STEPS } from 'FormsUI/constants/NavigationSteps';
import { FRAMEWORK_TABLE_PARAMS } from 'FormsUI/constants/QueryParams';
import { getFullUrl } from 'hubspot-url-utils';
/**
 * Internal routes
 */

function getFormsV2RoutePrefix() {
  const portalId = PortalIdParser.get();
  return `/forms/${portalId}`;
}
function getFormsRoutePrefix() {
  const portalId = PortalIdParser.get();
  return `/forms/${portalId}`;
}
export function getDashboardAppRoutePrefix({
  path,
  search
}) {
  const portalId = PortalIdParser.get();
  return `/forms-dashboard-ui/${portalId}${path || ''}${search || ''}`;
}
export function isAppRoute(pathname) {
  return pathname.indexOf(getFormsRoutePrefix()) === 0 || pathname.indexOf(getFormsV2RoutePrefix()) === 0;
}
export function getDashboardRoute(tab = '', location = {
  search: ''
}) {
  return `${getFormsV2RoutePrefix()}/${tab}${location.search}`;
}
export const getDashboardViewRoute = id => id ? `${getDashboardRoute()}views/${id}` : getDashboardRoute();
export const getDashboardFolderRoute = (uiMode = UI_MODE.LIST) => uiMode === UI_MODE.FOLDERS ? `${getDashboardRoute()}folders` : getDashboardRoute();
export const getCurrentUrl = () => {
  return `${getWindowLocationPathname()}${getWindowLocationSearch()}`;
};
export const getV2EditorRouteQueryString = ({
  redirectUrl
}) => {
  const search = getWindowLocationSearch();
  const searchSanitized = getFilteredSearchParams(search, FRAMEWORK_TABLE_PARAMS);
  const queryString = redirectUrl ? getUpdatedSearchParams(searchSanitized, 'redirectUrl', redirectUrl) : searchSanitized;
  return queryString;
};
export const getV2EditorRoute = (guid, step = STEPS.FORM, redirectUrl = '', options = {}) => {
  const fieldPath = options.field ? `/field/${options.field}` : '';
  const queryString = getV2EditorRouteQueryString({
    redirectUrl
  });
  return `${getFormsV2RoutePrefix()}/editor/${guid}/edit/${step}${fieldPath}${queryString}`;
};
export const getV4EditorRoute = (guid, launchMode) => {
  const baseUrl = `${getFormsRoutePrefix()}/new-editor/${guid}`;
  switch (launchMode) {
    case 'firstLaunch':
      return `${baseUrl}?firstLaunch=true`;
    case 'onboarding':
      return `${baseUrl}?onboarding=true`;
    case 'cloned':
      return `${baseUrl}?cloned=true`;
    default:
      return baseUrl;
  }
};
export const getFormEditorWithParams = (guid, params) => {
  const urlWithParams = new URL(`${getWindowOrigin()}${getV2EditorRoute(guid)}`);
  for (const property in params) {
    if (Object.prototype.hasOwnProperty.call(params, property)) {
      urlWithParams.searchParams.set(property, params[property]);
    }
  }
  return urlWithParams;
};
export const isSubmissionsRoute = route => {
  const urlRegex = `/forms/([0-9]+)/([0-9a-z-]*)/(performance|analyze|submissions)(.*)`;
  const regex = new RegExp(urlRegex);
  return route.match(regex);
};
export const getCallsToActionRoute = () => `/calls-to-action/${PortalIdParser.get()}`;
export const getCMSRoute = () => `/website/${PortalIdParser.get()}/pages/site/all`;
export function getSubmissionsRoute(guid, tab = Tabs.SUBMISSIONS) {
  switch (tab) {
    case Tabs.PERFORMANCE:
      return `${getFormsV2RoutePrefix()}/${guid}/performance`;
    case Tabs.ANSWERS:
      return `${getFormsV2RoutePrefix()}/${guid}/analyze`;
    default:
      return `${getFormsV2RoutePrefix()}/${guid}/submissions`;
  }
}
export const getSubmissionsToSubmissionsAppUrl = href => {
  const routeMatch = href ? isSubmissionsRoute(href) : null;
  if (!routeMatch) {
    return href;
  } else {
    const [__url, __portalId, formId, tab, queryString] = routeMatch;
    const submissionsUrl = getSubmissionsAppRoute(formId, tab.toUpperCase());
    return `${submissionsUrl}${queryString}`;
  }
};
export function getPerformanceRoute(guid) {
  return getSubmissionsRoute(guid, Tabs.PERFORMANCE);
}
export function removeConversionId(guid) {
  return `${getSubmissionsRoute(guid)}${window.location.search}`;
}
export function getSubmissionRoute(guid, submission) {
  return `${getSubmissionsRoute(guid)}/${submission}${window.location.search}`;
}
export function getTemplatesRoute(formType = FormCreationTypes.NEW_EDITOR) {
  return `${getFormsV2RoutePrefix()}/templates?formType=${formType}`;
}
export function getFormTypeRoute() {
  return `${getFormsV2RoutePrefix()}/type`;
}

/**
 * External routes
 */

export function getCoreDashboardRoutePrefix() {
  const portalId = PortalIdParser.get();
  return `/dashboard/${portalId}`;
}
function getContactsRoutePrefix(path) {
  const portalId = PortalIdParser.get();
  return `/contacts/${portalId}/${path}`;
}
function getPropertiesRoutePrefix(path) {
  const portalId = PortalIdParser.get();
  return `/property-settings/${portalId}/${path}`;
}
export function getCRMRoute(contactId) {
  return getContactsRoutePrefix(`contact/${contactId}`);
}
export function getPropertyEditorRoute(name, propertyObjectType) {
  if (propertyObjectType === PropertyObjectTypes.TICKET) {
    return getPropertiesRoutePrefix(`ticket/${name}`);
  }
  return getPropertiesRoutePrefix(`contact/${name}`);
}
export const getAbmPropertyRoute = (name, objectTypeId) => getPropertiesRoutePrefix(`properties?type=${objectTypeId}&property=${name}&action=edit`);
export function getPersonaEditorRoute() {
  return getContactsRoutePrefix('/personas');
}
export function getEmailSubscriptionTypesSettingsRoute() {
  const portalId = PortalIdParser.get();
  return `/settings/${portalId}/marketing/email/types`;
}
export function getLegalConsentSettingsRoute() {
  const portalId = PortalIdParser.get();
  return `/settings/${portalId}/data-privacy/consent-options`;
}
export function getCollectedFormsSettingsPage() {
  const portalId = PortalIdParser.get();
  return `/settings/${portalId}/marketing/form/non-hubspot`;
}
export function getFormStyleSettingsRoute() {
  const portalId = PortalIdParser.get();
  return `/settings/${portalId}/marketing/form/styles`;
}
export const getUserPreferencesProfileRoute = () => {
  const portalId = PortalIdParser.get();
  return `/settings/${portalId}/user-preferences/profile`;
};
export const getNotificationPreferencesRoute = () => `/notification-preferences/${PortalIdParser.get()}`;
export function getPopupEditorRoute(guid, redirectUrl = getDashboardRoute()) {
  const portalId = PortalIdParser.get();
  const redirectQueryParam = redirectUrl ? `?redirectUrl=${encodeURIComponent(redirectUrl)}` : '';
  return `/lead-flows/${portalId}/edit/${guid}${redirectQueryParam}`;
}
export const getInboxLink = id => getInboxChannelsTableUrl({
  inboxId: id
});
export const getHelpdeskSettingsLink = () => `/helpdesk-settings/${PortalIdParser.get()}`;
export const getPaymentOptionsLink = () => `/settings/${PortalIdParser.get()}/sales/payments`;
export const getPaymentLinksUrl = () => `/payment-links/${PortalIdParser.get()}`;
export const getPaymentLinkPreviewUrl = () => '/payments/iframe/preview';
export const getQuarantineUrl = guid => `/submissions-spam/${PortalIdParser.get()}${guid ? `?guid=${guid}` : ''}`;
export function getContactListsFilterBranch(guid) {
  const filter = {
    filterBranches: [{
      filterBranches: [],
      filterBranchOperator: 'AND',
      filterBranchType: 'AND',
      filters: [{
        filterType: 'FORM_SUBMISSION',
        formId: guid
      }]
    }],
    filterBranchOperator: 'OR',
    filterBranchType: 'OR',
    filters: []
  };
  return JSON.stringify(filter);
}
export function getContactListsRoute(guid) {
  const portalId = PortalIdParser.get();
  return `/contacts/${portalId}/objectLists/create?objectTypeId=${encodeURIComponent('0-1')}&processingType=DYNAMIC&filterBranch=${encodeURIComponent(getContactListsFilterBranch(guid))}`;
}
export function getFormFileUploadsRoute(guid) {
  return Routes.fileManager(`form-uploads/${guid}`, undefined);
}
export const getMeetingsUrl = () => `/meetings/${PortalIdParser.get()}`;
export const getSchedulingPagePreviewUrl = () => `/meetings-preview/${PortalIdParser.get()}/`;
export const getNewFormsEditorBetaOptInRoute = () => `/product-updates/${PortalIdParser.get()}/all?update=13942209`;
export const getEditPropertyUrl = (objectTypeId, propertyName) => `/property-settings/${PortalIdParser.get()}/properties?action=edit&type=${objectTypeId}&property=${propertyName}`;
export const getMarketingAnalyticsUrl = () => `/reports-list/${PortalIdParser.get()}/templates/marketing/leadCapture`;
export function getInboxChannelsTableUrl({
  inboxId
}) {
  return `${getFullUrl('app')}/live-messages-settings/${PortalIdParser.get()}/inboxes/${inboxId}/channels`;
}
export function getFormsCreateUrlForHelpdeskSettings() {
  return `${getFullUrl('app')}/helpdesk-settings/${PortalIdParser.get()}/inboxes/forms/create`;
}